/**
 * VibrantLabDetailModal.tsx
 *
 * Vibrant lab detail view. Handles two sources:
 *   - 'vibrant'        an inbound result (matching legacy vibrant-lab-details.php):
 *                      documents + test results with visual range bars + details.
 *   - 'vibrant-order'  an outbound order (labs_vibrant_orders): ordered tests +
 *                      order metadata parsed from the stored HL7 + current
 *                      delivery status.
 *
 * The content is factored into VibrantLabDetailBody so it can render inline in
 * the patient-detail right sidebar (LabDetailPanel) and inside the modal shell.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Download, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { getLabDetail, downloadLabReport, resendVibrantOrder, type LabDetail } from '@/services/labs.service';
import { downloadLabResultFile } from '@/services/lab-orders.service';
import { getErrorMessage } from '@/services/apiClient';
import { useToast } from '@/hooks/useToast';
import { Badge, Button, ConfirmDialog, Spinner } from '@/components/ui';
import { VisualRangeBar, FLAG_STYLES } from './VisualRangeBar';
import { vibrantStatusVariant } from './vibrantOrderStatus';

const STATUS_COLORS: Record<string, { bg: string; label: string }> = {
  SC: { bg: '#51a1fb', label: 'In Process' },
  IP: { bg: '#51a1fb', label: 'In Process' },
  CM: { bg: '#83dba2', label: 'Complete' },
  A:  { bg: '#e48b00', label: 'Partial Results' },
  CA: { bg: '#ff9797', label: 'Cancelled' },
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * A collection date/time as the clinic wrote it ('YYYY-MM-DD', or with
 * 'THH:MM[:SS]'), with no timezone conversion: `new Date('YYYY-MM-DD')` is
 * UTC midnight, which Pacific shows as the previous day at 5:00 PM. Midnight
 * is what an order carries when no time was recorded, so it shows the date
 * alone rather than a 12:00 AM nobody chose.
 */
function formatCollection(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec((value ?? '').trim());
  if (!m) return formatDate(value);
  const [, y, mo, d, h = '00', mi = '00'] = m;
  const local = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  return h === '00' && mi === '00'
    ? local.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : local.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const REQUISITION_POLL_MS = 60_000;
/** How long after sending an unconfirmed order to keep checking for its summary. */
const UNCONFIRMED_POLL_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Vibrant drops the requisition (order summary) PDF minutes after it reads an
 * order, and that is what turns the order "Received by Vibrant". While the
 * order is waiting for it, or has gone unconfirmed within the last day, ask
 * again every minute so a late summary flips the badge without reopening the
 * drawer. Keyed on the derived status, not the raw delivery column: an order
 * whose send never finished stays "pending" there yet still turns received
 * when the summary arrives. An unconfirmed order is aged from when it was sent,
 * else when it was created.
 */
export function requisitionPollInterval(detail: LabDetail | undefined): number | false {
  if (!detail || detail.source !== 'vibrant-order') return false;
  if (detail.statusKey === 'awaiting') return REQUISITION_POLL_MS;
  if (detail.statusKey !== 'unconfirmed') return false;
  const summary = (detail.summary ?? {}) as Record<string, unknown>;
  const anchor = Date.parse(String(summary.sentDate || summary.createDate || ''));
  if (Number.isNaN(anchor) || Date.now() - anchor > UNCONFIRMED_POLL_WINDOW_MS) return false;
  return REQUISITION_POLL_MS;
}

interface OrderedTest {
  testCode: string;
  description: string;
  collectionDate: string | null;
  notes: string[];
}

export type VibrantDetailSource = 'vibrant' | 'vibrant-order';

interface VibrantLabDetailBodyProps {
  source: VibrantDetailSource;
  /** labVibrantResultID for 'vibrant', labVibrantOrderID for 'vibrant-order'. */
  id: number;
}

/**
 * Vibrant detail content. Self-contained: fetches its own data and adapts to
 * result vs. outbound-order shape. Renders as a full-height flex column so the
 * parent (modal card or sidebar panel) just needs to give it a height.
 */
export function VibrantLabDetailBody({ source, id }: VibrantLabDetailBodyProps) {
  const isOrder = source === 'vibrant-order';
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['tests', 'documents', 'details']));

  const { data: detail, isLoading, isError } = useQuery({
    queryKey: ['lab-detail', source, id],
    queryFn: () => getLabDetail(source, id),
    enabled: id > 0,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => requisitionPollInterval(query.state.data),
  });

  function toggleSection(key: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const summary = (detail?.summary ?? {}) as Record<string, unknown>;
  const orderedTests: OrderedTest[] = Array.isArray(summary.orderedTests) ? (summary.orderedTests as OrderedTest[]) : [];
  const deliveryProblem = typeof summary.deliveryProblem === 'string' && summary.deliveryProblem !== '' ? summary.deliveryProblem : null;
  const canResend = isOrder && detail?.canResend === true;

  const statusInfo = !isOrder && detail?.status ? (STATUS_COLORS[detail.status] ?? { bg: '#ccc', label: detail.status }) : null;

  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirmResend, setConfirmResend] = useState(false);
  const [resending, setResending] = useState(false);

  // Every outcome refreshes the order: a refusal can mean Vibrant confirmed it
  // meanwhile, and a failed send leaves it "Not sent" with Send again offered.
  // A rejection is caught here so the dialog closes on it too.
  async function sendAgain() {
    setResending(true);
    try {
      const result = await resendVibrantOrder(id);
      toast.success(result.message);
    } catch (err) {
      toast.error(getErrorMessage(err, "This order didn't reach Vibrant. Try sending it again. If it keeps happening, contact Prospera support."));
    } finally {
      setResending(false);
      void queryClient.invalidateQueries({ queryKey: ['lab-detail', source, id] });
      if (detail?.patientId) {
        void queryClient.invalidateQueries({ queryKey: ['patient-vibrant-orders', detail.patientId] });
      }
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Status header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-6 py-3 flex-shrink-0">
        <h2 className="text-base font-semibold text-slate-900">
          {isOrder ? 'Order' : 'Accession'} #{detail?.orderNumber ?? id}
          {detail?.patientName ? ` — ${detail.patientName}` : ''}
        </h2>
        {isOrder ? (
          <Badge variant={vibrantStatusVariant(detail?.statusKey)} size="sm">
            {detail?.status ?? 'Unknown'}
          </Badge>
        ) : statusInfo ? (
          <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: statusInfo.bg, color: '#fff' }}>
            {statusInfo.label}
          </span>
        ) : null}
        {isOrder && detail?.statusKey === 'unconfirmed' ? (
          <span className="text-xs text-slate-600">
            Vibrant hasn&apos;t confirmed this order. Check the Vibrant portal, and send it again if it isn&apos;t there.
          </span>
        ) : null}
        {canResend ? (
          <Button size="xs" variant="secondary" className="ml-auto" onClick={() => setConfirmResend(true)}>
            Send again
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        isOpen={confirmResend}
        onClose={() => setConfirmResend(false)}
        onConfirm={sendAgain}
        variant="warning"
        title="Send this order again?"
        message="Check the Vibrant portal first. If the order is already there, don't send it again. This sends the same order to Vibrant again."
        confirmLabel={resending ? 'Sending…' : 'Send again'}
        cancelLabel="Cancel"
      />

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : isError || !detail ? (
          <p className="text-sm text-red-600">Failed to load lab details.</p>
        ) : (
          <>
            {/* Documents: result reports, and on an order the requisition
                (order summary) PDF Vibrant drops at order-submission time. */}
            {detail.files.length > 0 && (
              <div>
                <button type="button" onClick={() => toggleSection('documents')} className="flex items-center gap-2 w-full text-left py-2">
                  {expandedSections.has('documents') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Documents</h3>
                </button>
                {expandedSections.has('documents') && (
                  <div className="space-y-1 ml-6">
                    {detail.files.map((file, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          // Prefer the per-row URL the backend embeds, so each
                          // document downloads its own file. Without this every
                          // row fell through to the result-keyed /report
                          // endpoint, which returns the newest PDF only - so all
                          // rows opened the same document.
                          if (file.url) {
                            void downloadLabResultFile(file.url).then((result) => {
                              if (!result) return;
                              const objectUrl = URL.createObjectURL(result.blob);
                              const a = document.createElement('a');
                              a.href = objectUrl;
                              a.download = result.filename || file.name || 'lab_file';
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(objectUrl);
                            });
                            return;
                          }
                          // Older rows persisted before per-file URLs existed.
                          void downloadLabReport('vibrant', id);
                        }}
                        className="flex items-center gap-2 w-full text-left rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                      >
                        <FileText className="h-4 w-4 text-red-500 shrink-0" />
                        <span className="text-slate-800">{file.label || file.name}</span>
                        <Download className="h-3.5 w-3.5 text-slate-400 ml-auto" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Why a failed order was not sent, in plain words (order) */}
            {isOrder && deliveryProblem ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {deliveryProblem}
              </div>
            ) : null}

            {/* Ordered tests (order) */}
            {isOrder && (
              <div>
                <button type="button" onClick={() => toggleSection('tests')} className="flex items-center gap-2 w-full text-left py-2">
                  {expandedSections.has('tests') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Ordered Tests</h3>
                </button>
                {expandedSections.has('tests') && (
                  <div className="ml-2">
                    {orderedTests.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">
                        {detail.testSummary || 'No ordered tests found in the order message.'}
                      </p>
                    ) : (
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-slate-100 text-sm">
                          <thead className="bg-slate-50">
                            <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">
                              <th className="px-4 py-2">Test</th>
                              <th className="px-4 py-2 whitespace-nowrap">Code</th>
                              <th className="px-4 py-2 whitespace-nowrap">Collected</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {orderedTests.map((t, i) => (
                              <tr key={i} className="align-top">
                                <td className="px-4 py-2 text-slate-800 break-words min-w-0">
                                  {t.description || t.testCode || '—'}
                                  {t.notes && t.notes.length > 0 && (
                                    <ul className="mt-1 list-disc pl-4 text-xs text-slate-500">
                                      {t.notes.map((n, ni) => <li key={ni}>{n}</li>)}
                                    </ul>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-slate-500 font-mono text-xs whitespace-nowrap">{t.testCode || '—'}</td>
                                <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{t.collectionDate ? formatCollection(t.collectionDate) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Test results (result only) */}
            {!isOrder && (
              <div>
                <button type="button" onClick={() => toggleSection('tests')} className="flex items-center gap-2 w-full text-left py-2">
                  {expandedSections.has('tests') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Tests</h3>
                </button>
                {expandedSections.has('tests') && (
                  <div className="space-y-4 ml-2">
                    {detail.sections.length === 0 && detail.items.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">No test results available.</p>
                    ) : (
                      detail.sections.map((section, si) => (
                        <div key={si} className="rounded-lg border border-slate-200 overflow-hidden">
                          <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center justify-between">
                            <span className="text-sm font-semibold text-emerald-900">{section.title}</span>
                            {section.subtitle && <span className="text-xs text-emerald-600">{section.subtitle}</span>}
                          </div>
                          {'observations' in section && Array.isArray((section as any).observations) && (section as any).observations.length > 0 ? (
                            <table className="min-w-full divide-y divide-slate-100 text-sm">
                              <thead className="bg-slate-50">
                                <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                  <th className="px-4 py-2">Test</th>
                                  <th className="px-4 py-2">Result</th>
                                  <th className="px-4 py-2">Ref. Range</th>
                                  <th className="px-4 py-2">Unit</th>
                                  <th className="px-4 py-2">Flags</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {((section as any).observations as any[]).map((obs: any, oi: number) => {
                                  const flag = obs.flag ?? '';
                                  const flagStyle = FLAG_STYLES[flag] ?? { bg: '#ffffff', text: '#000000', label: flag };
                                  const isDNR = obs.value === 'DNR';
                                  const isPEND = obs.value === 'PEND';

                                  return (
                                    <tr
                                      key={oi}
                                      style={{
                                        backgroundColor: isDNR ? '#f5f5f5' : isPEND ? '#e3e3e3' : flagStyle.bg,
                                        color: isDNR || isPEND ? '#666' : flagStyle.text,
                                        display: isDNR ? 'none' : undefined,
                                      }}
                                    >
                                      <td className="px-4 py-2 font-medium">{obs.label ?? obs.code}</td>
                                      <td className="px-4 py-2">
                                        <span className="font-semibold">{obs.value}</span>
                                        {obs.referenceRange && obs.value && (
                                          <VisualRangeBar value={obs.value} referenceRange={obs.referenceRange} abnormalFlag={flag} />
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-slate-500">{obs.referenceRange ?? '—'}</td>
                                      <td className="px-4 py-2 text-slate-500">{obs.unit ?? '—'}</td>
                                      <td className="px-4 py-2">
                                        {flag && flag !== 'N' ? (
                                          <span className="text-xs font-semibold">{flagStyle.label}</span>
                                        ) : '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <p className="px-4 py-3 text-sm text-slate-400 italic">No results for this test.</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Details */}
            {detail.summary && (
              <div>
                <button type="button" onClick={() => toggleSection('details')} className="flex items-center gap-2 w-full text-left py-2">
                  {expandedSections.has('details') ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Details</h3>
                </button>
                {expandedSections.has('details') && (
                  <div className="ml-6 rounded-lg border border-slate-200 p-4 grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-slate-700">Patient</h4>
                      <p><span className="text-slate-500">Name:</span> {detail.patientName ?? '—'}</p>
                      <p><span className="text-slate-500">DOB:</span> {detail.birthDate ?? '—'}</p>
                      <p><span className="text-slate-500">ID:</span> {detail.patientDisplayId ?? '—'}</p>
                      {isOrder && summary.patientGender ? <p><span className="text-slate-500">Gender:</span> {String(summary.patientGender)}</p> : null}
                      {isOrder && summary.patientPhone ? <p><span className="text-slate-500">Phone:</span> {String(summary.patientPhone)}</p> : null}
                      {isOrder && summary.patientAddress ? <p><span className="text-slate-500">Address:</span> {String(summary.patientAddress)}</p> : null}
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-slate-700">{isOrder ? 'Order' : 'Result'}</h4>
                      <p><span className="text-slate-500">{isOrder ? 'Accession #:' : 'Accession #:'}</span> {detail.orderNumber ?? '—'}</p>
                      {isOrder ? (
                        <>
                          <p><span className="text-slate-500">Status:</span> {detail.status ?? '—'}</p>
                          <p><span className="text-slate-500">Ordered:</span> {formatDate(String(summary.createDate ?? detail.orderedAt ?? ''))}</p>
                          {summary.sentDate ? <p><span className="text-slate-500">Sent:</span> {formatDate(String(summary.sentDate))}</p> : null}
                          {summary.collectionDate ? <p><span className="text-slate-500">Collection:</span> {formatCollection(String(summary.collectionDate))}</p> : null}
                          {summary.providerName ? <p><span className="text-slate-500">Provider:</span> {String(summary.providerName)}{summary.providerNpi ? ` (${String(summary.providerNpi)})` : ''}</p> : null}
                          {summary.locationTitle || summary.location ? <p><span className="text-slate-500">Location:</span> {String(summary.locationTitle || summary.location)}</p> : null}
                          {summary.billing ? <p><span className="text-slate-500">Billing:</span> {String(summary.billing)}</p> : null}
                          {summary.controlId ? <p><span className="text-slate-500">Control ID:</span> {String(summary.controlId)}</p> : null}
                        </>
                      ) : (
                        <>
                          <p><span className="text-slate-500">Received:</span> {formatDate(detail.orderedAt)}</p>
                          <p><span className="text-slate-500">Status:</span> {statusInfo?.label ?? detail.status ?? '—'}</p>
                          {summary.providerName ? <p><span className="text-slate-500">Provider:</span> {String(summary.providerName)}</p> : null}
                        </>
                      )}
                    </div>
                    {isOrder && (summary.noteInternal || summary.noteExternal) ? (
                      <div className="col-span-2 space-y-1 border-t border-slate-100 pt-3">
                        {summary.noteExternal ? <p><span className="text-slate-500">External notes:</span> {String(summary.noteExternal)}</p> : null}
                        {summary.noteInternal ? <p><span className="text-slate-500">Internal notes:</span> {String(summary.noteInternal)}</p> : null}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface VibrantLabDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** labVibrantResultID for a result, labVibrantOrderID for an order. */
  labResultId: number;
  source?: VibrantDetailSource;
}

export function VibrantLabDetailModal({ open, onClose, labResultId, source = 'vibrant' }: VibrantLabDetailModalProps) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-8">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-end border-b border-slate-200 px-3 py-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <VibrantLabDetailBody source={source} id={labResultId} />
        </div>
      </div>
    </div>
  );
}
